import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { motion } from "framer-motion";
import { FaCoins, FaEdit, FaTrash } from "react-icons/fa";

// Giá trị mặc định cho newPackage
const DEFAULT_PACKAGE = {
  name: "",
  coinAmount: 0,
  price: 0,
  description: "",
};

// Component chính
const AdminCoinPackages = () => {
  const { fetchWithRetry } = useAuth();
  const [coinPackages, setCoinPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [newPackage, setNewPackage] = useState(DEFAULT_PACKAGE);
  const [actionType, setActionType] = useState(""); // "add" hoặc "edit"

  // Lấy danh sách gói coin
  const fetchCoinPackages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails(null);
      const response = await fetchWithRetry({
        method: "get",
        url: "/api/admin/coins",
      });
      setCoinPackages(response.data || []);
    } catch (err) {
      setError(err.message || "Không thể tải danh sách gói coin!");
      setErrorDetails(err.response?.data || null);
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  useEffect(() => {
    fetchCoinPackages();
  }, [fetchCoinPackages]);

  // Thêm gói coin mới
  const handleAddPackage = useCallback(async () => {
    try {
      await fetchWithRetry({
        method: "post",
        url: "/api/admin/coins",
        data: newPackage,
      });
      await fetchCoinPackages(); // Tải lại danh sách từ server
      setShowAddModal(false);
      setShowConfirmModal(false);
      setNewPackage(DEFAULT_PACKAGE);
      setSuccessMessage("Thêm gói coin thành công!");
    } catch (err) {
      setError(err.message || "Không thể thêm gói coin!");
      setErrorDetails(err.response?.data || null);
    }
  }, [fetchWithRetry, newPackage, fetchCoinPackages]);

  // Sửa gói coin
  const handleEditPackage = useCallback(async () => {
    try {
      await fetchWithRetry({
        method: "put",
        url: `/api/admin/coins/${selectedPackage._id}`,
        data: newPackage,
      });
      await fetchCoinPackages(); // Tải lại danh sách từ server
      setShowEditModal(false);
      setShowConfirmModal(false);
      setSelectedPackage(null);
      setNewPackage(DEFAULT_PACKAGE);
      setSuccessMessage("Sửa gói coin thành công!");
    } catch (err) {
      setError(err.message || "Không thể sửa gói coin!");
      setErrorDetails(err.response?.data || null);
    }
  }, [fetchWithRetry, newPackage, selectedPackage, fetchCoinPackages]);

  // Xóa gói coin
  const handleDeletePackage = useCallback(async (packageId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa gói coin này?")) return;
    try {
      await fetchWithRetry({
        method: "delete",
        url: `/api/admin/coins/${packageId}`,
      });
      await fetchCoinPackages(); // Tải lại danh sách từ server
      setSuccessMessage("Xóa gói coin thành công!");
    } catch (err) {
      setError(err.message || "Không thể xóa gói coin!");
      setErrorDetails(err.response?.data || null);
    }
  }, [fetchWithRetry, fetchCoinPackages]);

  if (loading) {
    return <p className="text-center text-gray-600 text-lg">Đang tải...</p>;
  }

  return (
    <div className="p-8 min-h-screen bg-gradient-to-br from-blue-50 to-gray-100">
      <h2 className="text-4xl font-extrabold text-blue-700 mb-8 text-center flex items-center justify-center">
        <FaCoins className="mr-3 text-yellow-500 text-4xl" /> Quản lý gói Coin
      </h2>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between shadow-md">
          <div>
            <span className="font-medium">{error}</span>
            {errorDetails && (
              <p className="text-sm mt-1">
                Chi tiết: {JSON.stringify(errorDetails)}
              </p>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setError(null);
                setErrorDetails(null);
                fetchCoinPackages();
              }}
              className="px-4 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-300"
            >
              Thử lại
            </button>
            <button
              onClick={() => {
                setError(null);
                setErrorDetails(null);
              }}
              className="px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-300"
            >
              Đóng
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg flex items-center justify-between shadow-md">
          <span className="font-medium">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-300"
          >
            Đóng
          </button>
        </div>
      )}

      {/* Nút thêm gói coin */}
      <div className="mb-8 text-right">
        <button
          onClick={() => setShowAddModal(true)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md"
        >
          Thêm gói Coin
        </button>
      </div>

      {/* Danh sách gói coin */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="overflow-x-auto rounded-lg shadow-lg"
      >
        <table className="w-full bg-white rounded-lg">
          <thead>
            <tr className="bg-blue-500 text-white">
              <th className="p-4 text-left text-lg font-semibold">Tên gói</th>
              <th className="p-4 text-left text-lg font-semibold">Số Coin</th>
              <th className="p-4 text-left text-lg font-semibold">Giá (VND)</th>
              <th className="p-4 text-left text-lg font-semibold">Mô tả</th>
              <th className="p-4 text-left text-lg font-semibold">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {coinPackages.map((pkg) => (
              <tr
                key={pkg._id}
                className="border-b hover:bg-gray-50 transition duration-200"
              >
                <td className="p-4 text-gray-800 text-base">{pkg.name}</td>
                <td className="p-4 text-gray-800 text-base">
                  <FaCoins className="inline mr-2 text-yellow-500" />{" "}
                  {pkg.coinAmount || 0}
                </td>
                <td className="p-4 text-gray-800 text-base">
                  {typeof pkg.price === "number"
                    ? pkg.price.toLocaleString()
                    : "N/A"}
                </td>
                <td className="p-4 text-gray-800 text-base">
                  {pkg.description || "Không có mô tả"}
                </td>
                <td className="p-4 text-gray-800 text-base flex space-x-3">
                  <button
                    onClick={() => {
                      setSelectedPackage(pkg);
                      setNewPackage({
                        name: pkg.name || "",
                        coinAmount: pkg.coinAmount || 0,
                        price: pkg.price || 0,
                        description: pkg.description || "",
                      });
                      setShowEditModal(true);
                    }}
                    className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition duration-300 shadow-sm"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeletePackage(pkg._id)}
                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-300 shadow-sm"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      {/* Modal thêm gói coin */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Thêm gói Coin
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setActionType("add");
                setShowConfirmModal(true);
              }}
              className="space-y-6"
            >
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Tên gói
                </label>
                <input
                  type="text"
                  value={newPackage.name}
                  onChange={(e) =>
                    setNewPackage({ ...newPackage, name: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Số Coin
                </label>
                <input
                  type="number"
                  value={newPackage.coinAmount}
                  onChange={(e) =>
                    setNewPackage({
                      ...newPackage,
                      coinAmount: Number(e.target.value),
                    })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Giá (VND)
                </label>
                <input
                  type="number"
                  value={newPackage.price}
                  onChange={(e) =>
                    setNewPackage({
                      ...newPackage,
                      price: Number(e.target.value),
                    })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Mô tả
                </label>
                <textarea
                  value={newPackage.description}
                  onChange={(e) =>
                    setNewPackage({
                      ...newPackage,
                      description: e.target.value,
                    })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  rows="3"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-300 shadow-sm"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-sm"
                >
                  Thêm
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal sửa gói coin */}
      {showEditModal && selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Sửa gói Coin
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setActionType("edit");
                setShowConfirmModal(true);
              }}
              className="space-y-6"
            >
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Tên gói
                </label>
                <input
                  type="text"
                  value={newPackage.name}
                  onChange={(e) =>
                    setNewPackage({ ...newPackage, name: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Số Coin
                </label>
                <input
                  type="number"
                  value={newPackage.coinAmount}
                  onChange={(e) =>
                    setNewPackage({
                      ...newPackage,
                      coinAmount: Number(e.target.value),
                    })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Giá (VND)
                </label>
                <input
                  type="number"
                  value={newPackage.price}
                  onChange={(e) =>
                    setNewPackage({
                      ...newPackage,
                      price: Number(e.target.value),
                    })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Mô tả
                </label>
                <textarea
                  value={newPackage.description}
                  onChange={(e) =>
                    setNewPackage({
                      ...newPackage,
                      description: e.target.value,
                    })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  rows="3"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-300 shadow-sm"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-sm"
                >
                  Lưu
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal xác nhận trước khi thêm/sửa */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Xác nhận {actionType === "add" ? "thêm" : "sửa"} gói Coin
            </h3>
            <p className="text-gray-700 mb-4">
              Bạn có chắc chắn muốn {actionType === "add" ? "thêm" : "sửa"} gói
              coin với thông tin sau?
            </p>
            <div className="space-y-2">
              <p className="text-gray-800">
                <strong className="font-medium">Tên gói:</strong>{" "}
                {newPackage.name}
              </p>
              <p className="text-gray-800">
                <strong className="font-medium">Số Coin:</strong>{" "}
                {newPackage.coinAmount}
              </p>
              <p className="text-gray-800">
                <strong className="font-medium">Giá (VND):</strong>{" "}
                {newPackage.price.toLocaleString()}
              </p>
              <p className="text-gray-800">
                <strong className="font-medium">Mô tả:</strong>{" "}
                {newPackage.description || "Không có mô tả"}
              </p>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-300 shadow-sm"
              >
                Hủy
              </button>
              <button
                onClick={
                  actionType === "add" ? handleAddPackage : handleEditPackage
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-sm"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoinPackages;